import { useEffect, useRef, useState } from 'react';
import { Game } from '../game/Game';
import { detectQuality, QUALITY_PROFILES } from '../engine/quality';
import { useGameStore } from '../state/store';
import { Hud } from './Hud';
import { OrientationGate } from './OrientationGate';
import { PauseMenu } from './PauseMenu';
import { PerfOverlay } from './PerfOverlay';
import { TouchControls } from './TouchControls';
import { StartScreen } from './StartScreen';
import { PwaPrompt } from './PwaPrompt';
import { getVehicleSpec } from '../vehicles/vehicleData';

export function App(): JSX.Element {
  const [game, setGame] = useState<Game | null>(null);
  const [error, setError] = useState<string | null>(null);
  const startedRef = useRef(false);

  const phase = useGameStore((s) => s.phase);
  const setPhase = useGameStore((s) => s.setPhase);
  const controlMode = useGameStore((s) => s.controlMode);
  const quality = useGameStore((s) => s.quality);
  const qualityAuto = useGameStore((s) => s.qualityAuto);
  const setQuality = useGameStore((s) => s.setQuality);
  const vehicleId = useGameStore((s) => s.vehicleId);
  const paintColor = useGameStore((s) => s.paintColor);
  const cameraMode = useGameStore((s) => s.cameraMode);
  const vibration = useGameStore((s) => s.vibration);
  const showPerf = useGameStore((s) => s.showPerf);
  const sound = useGameStore((s) => s.sound);
  const gameMode = useGameStore((s) => s.gameMode);
  const setVehicle = useGameStore((s) => s.setVehicle);

  // --- Spiel genau einmal erzeugen ---
  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    const store = useGameStore.getState();
    const level = store.qualityAuto ? detectQuality() : store.quality;
    if (store.qualityAuto) store.setQuality(level, true);

    const canvas = document.getElementById('game-canvas');
    if (!(canvas instanceof HTMLCanvasElement)) {
      setError('Canvas nicht gefunden');
      return;
    }

    let disposed = false;
    let created: Game | null = null;

    void store.hydrateProgress().then(() => {
      const readyStore = useGameStore.getState();
      return Game.create({
        canvas,
        quality: level,
        vehicleId: readyStore.vehicleId,
        paintColor: readyStore.paintColor,
        cameraMode: readyStore.cameraMode,
        gameMode: readyStore.gameMode,
        collectedIds: readyStore.collected,
        onCollect: (id) => useGameStore.getState().collect(id),
        onMissionComplete: (id, coins, stars) => useGameStore.getState().completeMission(id, coins, stars),
        onCameraModeChange: (mode) => useGameStore.getState().setCameraMode(mode),
      });
    })
      .then((g) => {
        if (disposed) {
          g.dispose();
          return;
        }
        created = g;
        g.perf.snapshot.quality = QUALITY_PROFILES[level].label;
        g.start();
        setGame(g);
        useGameStore.getState().setReady(true);
        useGameStore.getState().setPhase('home');
        document.getElementById('boot')?.classList.add('hidden');
        window.setTimeout(() => document.getElementById('boot')?.remove(), 500);
      })
      .catch((e: unknown) => {
        console.error(e);
        document.getElementById('boot')?.remove();
        setError(e instanceof Error ? e.message : 'Unbekannter Fehler');
      });

    return () => {
      disposed = true;
      created?.dispose();
    };
  }, []);

  // --- Einstellungen an die Engine durchreichen ---
  useEffect(() => {
    game?.setQuality(quality);
  }, [game, quality]);

  useEffect(() => {
    if (game && game.currentVehicleId !== vehicleId) game.swapVehicle(vehicleId);
  }, [game, vehicleId]);

  useEffect(() => {
    if (!game) return;
    if (gameMode === 'parking' && getVehicleSpec(vehicleId).category !== 'car') {
      setVehicle('stadtflitzer');
    }
    game.setGameMode(gameMode);
  }, [game, gameMode, vehicleId, setVehicle]);

  useEffect(() => {
    // Baut die Karosserie neu auf; ignoriert unveraenderte Farben.
    game?.setPaintColor(paintColor);
  }, [game, paintColor]);

  useEffect(() => {
    game?.setCameraMode(cameraMode);
  }, [game, cameraMode]);

  useEffect(() => { game?.setMuted(!sound); }, [game, sound]);

  // Beim Moduswechsel haengende Eingaben loeschen (sonst bleibt z. B. der
  // letzte Lenkeinschlag stehen).
  useEffect(() => {
    game?.input.clearTransient();
  }, [game, controlMode]);

  useEffect(() => {
    if (!game) return;
    game.paused = phase !== 'playing';
    if (phase !== 'playing') game.input.clearTransient();
  }, [game, phase]);

  // Automatik-Qualitaet: bleibt die Bildrate dauerhaft niedrig, eine Stufe
  // runter. Nur wenn der Nutzer nichts manuell gewaehlt hat.
  useEffect(() => {
    if (!game || !qualityAuto) return;
    const id = window.setInterval(() => {
      const fps = game.perf.snapshot.fps;
      if (fps === 0) return;
      const current = useGameStore.getState().quality;
      if (fps < 34 && current !== 'low') {
        setQuality(current === 'high' ? 'medium' : 'low', true);
      }
    }, 6000);
    return () => clearInterval(id);
  }, [game, qualityAuto, setQuality]);

  if (error) {
    return (
      <div className="overlay">
        <div className="card">
          <h1>OJE</h1>
          <p>Das Spiel konnte nicht starten: {error}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <OrientationGate />
      <PwaPrompt />
      {game && phase === 'home' && <StartScreen onStart={() => {
        game.enableAudio();
        const root = document.documentElement;
        if (!document.fullscreenElement && root.requestFullscreen) {
          void root.requestFullscreen({ navigationUI: 'hide' }).catch(() => undefined);
        }
        setPhase('playing');
      }} />}
      {game && phase === 'playing' && (
        <div className="hud">
          <Hud onPause={() => setPhase('paused')} />
          <TouchControls hub={game.input} controlMode={controlMode} vibrate={vibration} />
        </div>
      )}
      {game && phase === 'paused' && <PauseMenu onResume={() => setPhase('playing')} />}
      {showPerf && <PerfOverlay />}
    </>
  );
}
